import { mainEndpoints } from '@/lib/api/endpoints'
import { mainApi } from '@/lib/api/http'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'
import type { PageResult } from '@/lib/api/common'

import type { SaveBranchBody, Tenant, TenantBranch, UpdateTenantBody } from '../types'

/* -------------------------------------------------------------------------- */
/* The business                                                                */
/* -------------------------------------------------------------------------- */

/** The signed-in merchant's own tenant. The id comes off the token, not the url. */
export async function fetchTenant(): Promise<Tenant> {
  return await mainApi.get<Tenant>(mainEndpoints.tenant.root)
}

export async function updateTenant(body: UpdateTenantBody): Promise<Tenant> {
  return await mainApi.put<Tenant>(mainEndpoints.tenant.root, body)
}

/* -------------------------------------------------------------------------- */
/* Branches                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Every branch, in one call.
 *
 * Branches are picked from — by hours, tables, orders and the staff screen — far
 * more often than they are paged through, and a tenant has a handful of them.
 * The endpoint is paged all the same, so the cap is stated rather than assumed.
 */
export async function fetchBranches(): Promise<TenantBranch[]> {
  const page = await mainApi.get<PageResult<TenantBranch>>(mainEndpoints.tenant.branches, {
    params: { limit: PAGE_LIMIT_MAX, offset: 0 },
  })

  return page.rows ?? []
}

export async function createBranch(body: SaveBranchBody): Promise<TenantBranch> {
  return await mainApi.post<TenantBranch>(mainEndpoints.tenant.branches, body)
}

export async function updateBranch(id: string, body: SaveBranchBody): Promise<TenantBranch> {
  return await mainApi.put<TenantBranch>(`${mainEndpoints.tenant.branches}/${id}`, body)
}

export async function deleteBranch(id: string): Promise<void> {
  await mainApi.delete<void>(`${mainEndpoints.tenant.branches}/${id}`)
}

/** Disabling hides the branch from the storefront without deleting its history. */
export async function setBranchEnabled(id: string, enabled: boolean): Promise<void> {
  await mainApi.patch<void>(
    `${mainEndpoints.tenant.branches}/${id}/${enabled ? 'enable' : 'disable'}`,
  )
}
